import { useState } from 'react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/contexts/I18nContext';
import { useApi } from '@/hooks/useApi';
import type { ExceptionIncident } from '@/types/exception.types';

interface ExceptionIncidentDetailProps {
  incident: ExceptionIncident | null;
  open: boolean;
  onClose: () => void;
  onResolved: () => void;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString();
}

export function ExceptionIncidentDetail({ incident, open, onClose, onResolved }: ExceptionIncidentDetailProps) {
  const { tp } = useI18n();
  const { patch } = useApi();
  const [note, setNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const handleResolve = async () => {
    if (!incident) return;
    setResolving(true);
    try {
      await patch(`/api/v1/admin/exception-incidents/${incident.id}/resolve`, { note });
      toast.success(tp.exceptions.resolve.success);
      onResolved();
      onClose();
    } catch {
      // error already handled by useApi
    } finally {
      setResolving(false);
    }
  };

  if (!incident) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b shrink-0">
          <SheetTitle className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{incident.error_code}</Badge>
            <span className="text-sm font-medium">{incident.module}</span>
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              {incident.occurrence_count}x
            </Badge>
            {incident.resolved ? (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                {tp.exceptions.columns.resolved}
              </Badge>
            ) : (
              <Badge variant="destructive">{tp.exceptions.columns.open}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-muted-foreground">{tp.exceptions.columns.firstSeen}</p>
              <p className="font-medium">{formatDate(incident.first_seen_at)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{tp.exceptions.columns.lastSeen}</p>
              <p className="font-medium">{formatDate(incident.last_seen_at)}</p>
            </div>
          </div>

          {(incident.origin_file || incident.origin_function) && (
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-1">{tp.exceptions.detail.origin}</p>
              {incident.origin_file && (
                <code className="text-xs bg-muted rounded px-2 py-1 block">
                  {incident.origin_file}{incident.origin_line != null ? `:${incident.origin_line}` : ''}
                  {incident.origin_function ? ` — ${incident.origin_function}` : ''}
                </code>
              )}
            </div>
          )}

          {!incident.resolved && (
            <div className="border rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold">{tp.exceptions.resolve.title}</p>
              <Textarea
                placeholder={tp.exceptions.resolve.notePlaceholder}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="text-sm resize-none"
              />
              <Button onClick={handleResolve} disabled={resolving} size="sm">
                {resolving ? tp.exceptions.resolve.resolving : tp.exceptions.resolve.button}
              </Button>
            </div>
          )}

          {incident.resolved && (
            <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  {tp.exceptions.columns.resolved}
                </Badge>
                {incident.resolved_at && (
                  <span className="text-xs text-muted-foreground">{formatDate(incident.resolved_at)}</span>
                )}
              </div>
              {incident.resolution_note && (
                <p className="text-sm text-muted-foreground">{incident.resolution_note}</p>
              )}
            </div>
          )}

          {incident.recent_logs && incident.recent_logs.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">{tp.exceptions.detail.recentLogs}</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-start px-3 py-2 font-medium">{tp.exceptions.columns.time}</th>
                      <th className="text-start px-3 py-2 font-medium">Correlation</th>
                      <th className="text-start px-3 py-2 font-medium">{tp.exceptions.detail.userId}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incident.recent_logs.map((log) => (
                      <tr key={log.id} className="border-t">
                        <td className="px-3 py-2 font-mono">{new Date(log.occurred_at).toLocaleString()}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">
                          {log.correlation_id ? log.correlation_id.slice(0, 12) + '…' : '—'}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {log.user_id ? log.user_id.slice(0, 8) + '…' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
